import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { FileUp, ImagePlus, Loader2, Save, Trash2, X } from 'lucide-react'
import { Button } from '@orisini/ui/components/button'
import { Card } from '@orisini/ui/components/card'
import { Input } from '@orisini/ui/components/input'
import { Label } from '@orisini/ui/components/label'
import { Textarea } from '@orisini/ui/components/textarea'
import { usePrompt, useConfirm } from '@orisini/ui/components/confirm'
import { cn } from '@orisini/ui/lib/utils'
import { accountsApi, type Workspace } from '../../accounts/lib/accounts'
import { shrinkPhoto, uploadPhoto } from '../lib/photos'
import { contentApi, DESTINATION_LABEL, type Destination, type Kind } from '../lib/content'
import { EMPTY_VIDEO, VideoMaterial, type VideoMaterialValue } from '../components/VideoMaterial'
import { applyMapping, emptyMapping, parseCsv, TARGET_FIELDS, type Csv, type FieldMap, type Mapping, type TargetKey } from '../lib/csv'

const MAX_ROWS = 200
const MAX_PER_BRIEF = 60

/**
 * /create — konten dari materi milik user sendiri (Discover = yang ditemukan
 * Orisini). Satu brief = materi + instruksi + pilihan produksi; CSV = banyak
 * brief sekaligus dengan pemetaan kolom bebas.
 */
export default function CreatePage() {
  const [params, setParams] = useSearchParams()
  const tab = params.get('tab') === 'csv' ? 'csv' : 'brief'
  const [workspaces, setWorkspaces] = useState<Workspace[] | null>(null)

  useEffect(() => {
    accountsApi.workspaces().then(setWorkspaces).catch(() => setWorkspaces([]))
  }, [])

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight">Create</h1>
        <p className="text-sm text-muted-foreground">Turn your own links, notes, photos, and audio into posts and videos. Orisini writes the drafts and designs them with each project's style.</p>
      </header>

      <div className="flex w-fit gap-1 rounded-md bg-muted p-1">
        {(
          [
            ['brief', 'New brief'],
            ['csv', 'Import CSV'],
          ] as const
        ).map(([k, label]) => (
          <button key={k} onClick={() => setParams(k === 'brief' ? {} : { tab: k }, { replace: true })} className={cn('rounded px-3 py-1.5 text-sm', tab === k ? 'bg-background font-medium shadow-sm' : 'text-muted-foreground')}>
            {label}
          </button>
        ))}
      </div>

      {workspaces === null ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" /> Loading…
        </div>
      ) : workspaces.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          <Link to="/projects/new" className="text-foreground underline-offset-4 hover:underline">Create a project</Link> first — content is always made for a project.
        </p>
      ) : tab === 'brief' ? (
        <BriefForm workspaces={workspaces} />
      ) : (
        <CsvImport workspaces={workspaces} />
      )}
    </div>
  )
}

function KindPicker({ kinds, onChange }: { kinds: Kind[]; onChange: (k: Kind[]) => void }) {
  return (
    <div className="flex flex-wrap gap-2">
      {(['poster', 'carousel', 'video'] as Kind[]).map((k) => (
        <label key={k} className={cn('flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm', kinds.includes(k) ? 'border-primary bg-primary/10' : 'border-border')}>
          <input type="checkbox" checked={kinds.includes(k)} onChange={() => onChange(kinds.includes(k) ? kinds.filter((x) => x !== k) : [...kinds, k])} />
          {k === 'poster' ? 'Poster' : k === 'carousel' ? 'Carousel' : 'Video'}
        </label>
      ))}
    </div>
  )
}

function DestinationSelect({ value, onChange, id }: { value: Destination | ''; onChange: (v: Destination | '') => void; id?: string }) {
  return (
    <select id={id} className="h-9 rounded-md border border-input bg-background px-3 text-sm" value={value} onChange={(e) => onChange(e.target.value as Destination | '')}>
      <option value="">Each project's workflow setting</option>
      {(Object.keys(DESTINATION_LABEL) as Destination[]).map((d) => (
        <option key={d} value={d}>{DESTINATION_LABEL[d]}</option>
      ))}
    </select>
  )
}

function BriefForm({ workspaces }: { workspaces: Workspace[] }) {
  const navigate = useNavigate()
  const [urls, setUrls] = useState('')
  const [title, setTitle] = useState('')
  const [text, setText] = useState('')
  const [notes, setNotes] = useState('')
  const [photo, setPhoto] = useState<{ id: string; name: string } | null>(null)
  const [accounts, setAccounts] = useState<string[]>(workspaces.length === 1 ? [workspaces[0]!.id] : [])
  const [kinds, setKinds] = useState<Kind[]>(['poster'])
  const [destination, setDestination] = useState<Destination | ''>('')
  const [video, setVideo] = useState<VideoMaterialValue>(EMPTY_VIDEO)
  const [busy, setBusy] = useState<'photo' | 'send' | null>(null)

  const daftarUrl = urls.split(/\s+/).map((u) => u.trim()).filter(Boolean)
  const jumlah = (daftarUrl.length || 1) * accounts.length * kinds.length
  const pakaiVideo = kinds.includes('video')
  const adaMateri = daftarUrl.length > 0 || text.trim() !== '' || title.trim() !== '' || (pakaiVideo && video.voiceover !== null)
  const durasiSah = !pakaiVideo || video.voiceover !== null || video.duration.mode !== 'custom' || (video.duration.minutes ?? 0) > 0
  const platformDipilih = [...new Set(workspaces.filter((w) => accounts.includes(w.id)).map((w) => w.platform).filter((p): p is string => p !== null))]

  const pilihFoto = async (file: File | undefined) => {
    if (!file) return
    setBusy('photo')
    try {
      const r = await uploadPhoto(await shrinkPhoto(file))
      setPhoto({ id: r.id, name: file.name })
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't upload the photo")
    } finally {
      setBusy(null)
    }
  }

  const kirim = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy('send')
    try {
      const r = await contentApi.brief({
        urls: daftarUrl,
        title,
        text,
        notes,
        imageAssetId: photo?.id ?? null,
        workspaceIds: accounts,
        kinds,
        destination: destination || null,
        ...(pakaiVideo
          ? {
              duration: video.voiceover ? null : video.duration,
              voiceoverAssetId: video.voiceover?.id ?? null,
              audio: video.audio.map((a) => ({ assetId: a.asset.id, role: a.role, label: a.label })),
              brollAssetIds: video.broll.map((b) => b.id),
            }
          : {}),
      })
      toast.success(`${r.created} ${r.created === 1 ? 'piece' : 'pieces'} of content started. Drafts appear in Review as soon as they're written.`)
      navigate('/bank?status=working')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't start the brief")
      setBusy(null)
    }
  }

  return (
    <form onSubmit={kirim} className="space-y-5">
      <Card className="space-y-4 p-5">
        <h2 className="font-medium">Material</h2>
        <div className="space-y-1.5">
          <Label htmlFor="b-urls">Links</Label>
          <Textarea id="b-urls" rows={3} value={urls} placeholder={'https://…\nhttps://…'} onChange={(e) => setUrls(e.target.value)} />
          <p className="text-xs text-muted-foreground">One per line. Each link becomes its own post. Orisini reads the whole article.</p>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="b-title">Title (optional)</Label>
          <Input id="b-title" value={title} maxLength={200} onChange={(e) => setTitle(e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="b-text">Your text (optional)</Label>
          <Textarea id="b-text" rows={5} maxLength={12000} value={text} placeholder="Notes, a script, a product description…" onChange={(e) => setText(e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <span className="text-sm font-medium">Photo (optional)</span>
          <div className="flex items-center gap-2">
            <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-sm hover:border-foreground/40">
              {busy === 'photo' ? <Loader2 className="size-4 animate-spin" /> : <ImagePlus className="size-4" />}
              {photo ? 'Replace photo' : 'Add photo'}
              <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => void pilihFoto(e.target.files?.[0])} />
            </label>
            {photo && (
              <span className="flex items-center gap-1 text-sm text-muted-foreground">
                {photo.name}
                <button type="button" onClick={() => setPhoto(null)} aria-label="Remove photo">
                  <X className="size-3.5" />
                </button>
              </span>
            )}
          </div>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="b-notes">Instructions (optional)</Label>
          <Textarea id="b-notes" rows={2} maxLength={1000} value={notes} placeholder="Focus on the price. End with a question." onChange={(e) => setNotes(e.target.value)} />
        </div>
      </Card>

      <Card className="space-y-4 p-5">
        <h2 className="font-medium">Production</h2>
        <div className="space-y-1.5">
          <span className="text-sm font-medium">Projects</span>
          <div className="flex flex-wrap gap-2">
            {workspaces.map((w) => (
              <label key={w.id} className={cn('flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm', accounts.includes(w.id) ? 'border-primary bg-primary/10' : 'border-border')}>
                <input type="checkbox" checked={accounts.includes(w.id)} onChange={() => setAccounts(accounts.includes(w.id) ? accounts.filter((x) => x !== w.id) : [...accounts, w.id])} />
                {w.name} {w.platform && <span className="text-xs text-muted-foreground">{w.platform}</span>}
              </label>
            ))}
          </div>
        </div>
        <div className="space-y-1.5">
          <span className="text-sm font-medium">Content type</span>
          <KindPicker kinds={kinds} onChange={setKinds} />
        </div>
        {pakaiVideo && <VideoMaterial value={video} onChange={setVideo} platforms={platformDipilih} />}
        <div className="space-y-1.5">
          <Label htmlFor="b-dest">After production</Label>
          <div>
            <DestinationSelect id="b-dest" value={destination} onChange={setDestination} />
          </div>
        </div>
      </Card>

      <div className="flex flex-wrap items-center gap-3">
        <Button type="submit" disabled={busy !== null || !adaMateri || !durasiSah || !accounts.length || !kinds.length || jumlah > MAX_PER_BRIEF}>
          {busy === 'send' && <Loader2 className="animate-spin" />}
          Create {jumlah > 0 && accounts.length && kinds.length ? jumlah : ''} {jumlah === 1 ? 'post' : 'posts'}
        </Button>
        {jumlah > MAX_PER_BRIEF && <span className="text-sm text-destructive">Keep it under {MAX_PER_BRIEF} at a time.</span>}
      </div>
    </form>
  )
}

function FieldMapper({
  target,
  headers,
  map,
  workspaces,
  onChange,
}: {
  target: (typeof TARGET_FIELDS)[number]
  headers: string[]
  map: FieldMap
  workspaces: Workspace[]
  onChange: (m: FieldMap) => void
}) {
  const nilai = map.source === 'column' ? `col:${map.column}` : map.source
  const tetap = map.source === 'fixed' ? map.value : ''
  const tetapInput =
    target.key === 'account' ? (
      <select className="h-9 rounded-md border border-input bg-background px-2 text-sm" value={tetap} onChange={(e) => onChange({ source: 'fixed', value: e.target.value })}>
        <option value="">Choose…</option>
        {workspaces.map((w) => (
          <option key={w.id} value={w.id}>{w.name}</option>
        ))}
      </select>
    ) : target.key === 'kind' ? (
      <select className="h-9 rounded-md border border-input bg-background px-2 text-sm" value={tetap} onChange={(e) => onChange({ source: 'fixed', value: e.target.value })}>
        <option value="poster">Poster</option>
        <option value="carousel">Carousel</option>
        <option value="video">Video</option>
        <option value="both">Poster + carousel</option>
      </select>
    ) : target.key === 'destination' ? (
      <DestinationSelect value={tetap as Destination | ''} onChange={(v) => onChange({ source: 'fixed', value: v })} />
    ) : (
      <Input className="h-9 w-56" value={tetap} onChange={(e) => onChange({ source: 'fixed', value: e.target.value })} />
    )
  return (
    <div className="grid items-center gap-2 py-2 sm:grid-cols-[10rem_14rem_1fr]">
      <div>
        <p className="text-sm font-medium">{target.label}</p>
        <p className="text-xs text-muted-foreground">{target.hint}</p>
      </div>
      <select
        className="h-9 rounded-md border border-input bg-background px-2 text-sm"
        value={nilai}
        onChange={(e) => {
          const v = e.target.value
          if (v === 'none') onChange({ source: 'none' })
          else if (v === 'fixed') onChange({ source: 'fixed', value: target.key === 'kind' ? 'poster' : '' })
          else onChange({ source: 'column', column: v.slice(4) })
        }}
      >
        <option value="none">— Not used —</option>
        {headers.map((h) => (
          <option key={h} value={`col:${h}`}>Column: {h}</option>
        ))}
        <option value="fixed">Same value for every row</option>
      </select>
      <div>{map.source === 'fixed' && tetapInput}</div>
    </div>
  )
}

function CsvImport({ workspaces }: { workspaces: Workspace[] }) {
  const navigate = useNavigate()
  const prompt = usePrompt()
  const confirm = useConfirm()
  const [file, setFile] = useState<{ name: string; csv: Csv } | null>(null)
  const [mapping, setMapping] = useState<Mapping | null>(null)
  const [presets, setPresets] = useState<{ id: string; name: string; mapping: unknown }[]>([])
  const [busy, setBusy] = useState(false)
  const [hasil, setHasil] = useState<{ created: number; rejected: { row: number; reason: string }[] } | null>(null)

  useEffect(() => {
    contentApi.presets().then(setPresets).catch(() => undefined)
  }, [])

  const accountRefs = useMemo(() => workspaces.map((w) => ({ id: w.id, name: w.name, username: w.account?.username ?? null })), [workspaces])
  const mapped = useMemo(() => (file && mapping ? applyMapping(file.csv, mapping, accountRefs) : []), [file, mapping, accountRefs])
  const valid = mapped.filter((r) => r.brief)

  const muatBerkas = async (f: File | undefined) => {
    if (!f) return
    try {
      const csv = parseCsv(await f.text())
      if (!csv.headers.length || !csv.rows.length) throw new Error('This file has no rows')
      if (csv.rows.length > MAX_ROWS) toast.info(`Only the first ${MAX_ROWS} rows are imported at a time`)
      const potong = { headers: csv.headers, rows: csv.rows.slice(0, MAX_ROWS) }
      setFile({ name: f.name, csv: potong })
      setMapping(emptyMapping(potong.headers))
      setHasil(null)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't read this file")
    }
  }

  const simpanPreset = async () => {
    if (!mapping) return
    const name = await prompt({ title: 'Save this mapping', label: 'Preset name', placeholder: 'e.g. Product sheet', validate: (v) => (v.trim() ? undefined : 'Give it a name') })
    if (!name) return
    try {
      await contentApi.savePreset(name.trim(), mapping)
      setPresets(await contentApi.presets())
      toast.success('Mapping saved')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't save the mapping")
    }
  }

  const hapusPreset = async (id: string, name: string) => {
    if (!(await confirm({ title: `Delete the preset "${name}"?`, confirmLabel: 'Delete', destructive: true }))) return
    await contentApi.deletePreset(id).catch(() => undefined)
    setPresets(await contentApi.presets())
  }

  const impor = async () => {
    if (!file || !mapping || !valid.length) return
    setBusy(true)
    try {
      const r = await contentApi.importRows(file.name, mapping, valid.map((v) => v.brief as unknown as Record<string, unknown>))
      // Server menomori baris yang DIKIRIM; kembalikan ke nomor baris di CSV.
      setHasil({ created: r.created, rejected: r.rejected.map((x) => ({ ...x, row: valid[x.row - 1]?.index ?? x.row })) })
      toast.success(`${r.accepted} rows imported — ${r.created} pieces of content started`)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't import")
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-5">
      <Card className="flex flex-wrap items-center gap-3 p-5">
        <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-border px-3 py-2 text-sm hover:border-foreground/40">
          <FileUp className="size-4" />
          {file ? 'Choose another file' : 'Choose a CSV file'}
          <input type="file" accept=".csv,text/csv,text/plain" className="hidden" onChange={(e) => void muatBerkas(e.target.files?.[0])} />
        </label>
        <p className="text-sm text-muted-foreground">
          {file ? `${file.name} · ${file.csv.rows.length} rows · ${file.csv.headers.length} columns` : 'Any columns, any order — you map them to fields next. Comma, semicolon, and tab files all work.'}
        </p>
      </Card>

      {file && mapping && (
        <>
          <Card className="space-y-2 p-5">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="font-medium">Map your columns</h2>
              <div className="flex flex-wrap items-center gap-2">
                {presets.length > 0 && (
                  <select
                    className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                    value=""
                    onChange={(e) => {
                      const p = presets.find((x) => x.id === e.target.value)
                      if (p) setMapping({ ...emptyMapping(file.csv.headers), ...(p.mapping as Mapping) })
                    }}
                  >
                    <option value="">Load a saved mapping…</option>
                    {presets.map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                )}
                <Button size="sm" variant="outline" onClick={simpanPreset}>
                  <Save /> Save mapping
                </Button>
              </div>
            </div>
            <div className="divide-y divide-border">
              {TARGET_FIELDS.map((t) => (
                <FieldMapper
                  key={t.key}
                  target={t}
                  headers={file.csv.headers}
                  map={mapping.fields[t.key as TargetKey]}
                  workspaces={workspaces}
                  onChange={(m) => setMapping({ ...mapping, fields: { ...mapping.fields, [t.key]: m } })}
                />
              ))}
            </div>
            <label className="flex items-center gap-2 pt-2 text-sm">
              <input type="checkbox" checked={mapping.extraToAi} onChange={(e) => setMapping({ ...mapping, extraToAi: e.target.checked })} />
              Send columns that aren't mapped to the AI as extra context
            </label>
            {presets.length > 0 && (
              <div className="flex flex-wrap gap-2 pt-2 text-xs text-muted-foreground">
                Saved:
                {presets.map((p) => (
                  <span key={p.id} className="inline-flex items-center gap-1">
                    {p.name}
                    <button onClick={() => void hapusPreset(p.id, p.name)} aria-label={`Delete ${p.name}`}>
                      <Trash2 className="size-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </Card>

          <section className="space-y-2">
            <h2 className="text-sm font-semibold">
              Preview — {valid.length} of {mapped.length} rows ready
            </h2>
            <div className="overflow-x-auto rounded-lg border border-border">
              <table className="w-full text-left text-xs">
                <thead className="bg-muted/50 text-muted-foreground">
                  <tr>
                    <th className="p-2">#</th>
                    <th className="p-2">Link / title</th>
                    <th className="p-2">Project</th>
                    <th className="p-2">Type</th>
                    <th className="p-2">After</th>
                    <th className="p-2">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {mapped.slice(0, 10).map((r) => (
                    <tr key={r.index}>
                      <td className="p-2 tabular-nums text-muted-foreground">{r.index}</td>
                      <td className="max-w-[18rem] truncate p-2">{r.summary.title || r.summary.url || '—'}</td>
                      <td className="p-2">{r.summary.account || '—'}</td>
                      <td className="p-2">{r.summary.kinds || '—'}</td>
                      <td className="p-2">{r.summary.destination}</td>
                      <td className={cn('p-2', r.error ? 'text-destructive' : 'text-emerald-400')}>{r.error ?? 'Ready'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {mapped.length > 10 && <p className="text-xs text-muted-foreground">Showing the first 10 rows. {mapped.length - valid.length} rows in total have a problem and will be skipped.</p>}
          </section>

          <div className="flex flex-wrap items-center gap-3">
            <Button onClick={impor} disabled={busy || !valid.length}>
              {busy && <Loader2 className="animate-spin" />}
              Import {valid.length} {valid.length === 1 ? 'row' : 'rows'}
            </Button>
            {hasil && (
              <Button variant="ghost" onClick={() => navigate('/bank?status=working')}>
                See progress in the Content Bank
              </Button>
            )}
          </div>
          {hasil && hasil.rejected.length > 0 && (
            <Card className="space-y-1 p-4 text-sm">
              <p className="font-medium text-destructive">Rows the server skipped</p>
              {hasil.rejected.map((r) => (
                <p key={r.row} className="text-xs text-muted-foreground">
                  Row {r.row}: {r.reason}
                </p>
              ))}
            </Card>
          )}
        </>
      )}
    </div>
  )
}
